import type { Float8_Comparison_Exp } from 'graphql/research/types-and-hooks';
import type { MapOrganSystems } from 'lib/queries/dashboard';
import type { PaginationProps } from './pagination-clientside';

import React, { memo, useCallback, useMemo, useReducer } from 'react';
import { useToast, Paragraph } from '@leafwell/components';

import { useOrganSystemsStudiesQuery } from 'graphql/research/types-and-hooks';
import { DASHBOARD_FILTERS, mapOrganSystems } from 'lib/queries/dashboard';
import { useWordPressContext } from 'contexts/wordpress-provider';
import { useDasboardContext } from 'contexts/dashboard-context';

import Pagination from './pagination-clientside';
import CardEmpty from './card-empty';
import Loader from './loader';
import LinkBadge from './link-badge';
import Figure from './figure';

type PaginatedSystems = {
  systems: MapOrganSystems;
  offset: number;
  total: number;
};

export type SystemsPaginatedAction =
  | {
      type: 'updateItems';
      value: Pick<PaginatedSystems, 'systems' | 'total'>;
    }
  | {
      type: 'changeOffset';
      value: number;
    }
  | {
      type: 'reset';
      value: Pick<PaginatedSystems, 'systems' | 'total'>;
    };

const SYSTEMS_PER_PAGE = 6;

export const paginatedSystemsReducer = (
  state: PaginatedSystems,
  action: SystemsPaginatedAction,
): PaginatedSystems => {
  switch (action.type) {
    case 'updateItems': {
      return {
        ...state,
        ...action.value,
      };
    }
    case 'changeOffset': {
      return {
        ...state,
        offset: action.value,
      };
    }
    case 'reset': {
      return {
        offset: 0,
        ...action.value,
      };
    }
  }
};

const OrganSystemsList: React.FC<
  Omit<PaginationProps, 'limit'> & { systems: MapOrganSystems }
> = memo(({ systems, ...pagination }) => {
  const { translations } = useWordPressContext();

  return systems.length ? (
    <section className="md:col-span-2 md:self-start grid gap-3">
      {systems.map(
        (
          {
            icon,
            name,
            studies: {
              aggregate: { count },
            },
          },
          key,
        ) => (
          <LinkBadge
            {...{
              key,
              className:
                'flex items-center justify-between gap-3 lg:gap-5 pointer-events-none md:text-base',
              href: '#',
              variant: 'secondary',
            }}
          >
            {icon && (
              <Figure
                {...{ ...icon, className: 'h-6 w-6', objectFit: 'contain' }}
              />
            )}
            <span className="grow">{name}</span>
            {`${count} ${
              count !== 1 ? translations['studies'] : translations['study']
            }`}
          </LinkBadge>
        ),
      )}
      <Pagination
        {...{
          className: 'mt-3',
          limit: SYSTEMS_PER_PAGE,
          variant: 'minimal',
          ...pagination,
        }}
      />
    </section>
  ) : (
    <CardEmpty className="col-span-full row-start-1 md:self-start" />
  );
});

const DashboardOrganSystems: React.FC<{ id: string }> = ({ id }) => {
  const { addToast } = useToast();
  const { conditionTypesImage, translations } = useWordPressContext();
  const {
    filters,
    initialData: {
      [DASHBOARD_FILTERS.ORGAN_SYSTEMS]: initialSystems,
      wp: { conditionTypes },
    },
    studies: { byYear },
  } = useDasboardContext();
  const getInitalData = useCallback(
    () => ({
      systems: mapOrganSystems({
        systems: initialSystems.nodes,
        wpData: conditionTypes,
      }),
      total: initialSystems.pagination.aggregate.count,
    }),
    [conditionTypes, initialSystems],
  );
  const [{ systems, offset, total }, updateSettings] = useReducer(
    paginatedSystemsReducer,
    {
      offset: 0,
      ...getInitalData(),
    },
  );
  const studyIds = useMemo(() => byYear.map(({ id }) => id), [byYear]);
  const variables = useMemo(
    () => ({
      offset,
      ...(filters?.[DASHBOARD_FILTERS.ORGAN_SYSTEMS]?.organ_system_id
        ? {
            systemIds: filters[DASHBOARD_FILTERS.ORGAN_SYSTEMS].organ_system_id,
          }
        : {}),
      ...(filters
        ? { studyIds: { _in: studyIds } as Float8_Comparison_Exp }
        : {}),
    }),
    [filters?.[DASHBOARD_FILTERS.ORGAN_SYSTEMS], offset, studyIds],
  );

  const showInitialData = useMemo(
    () => !filters && offset === 0,
    [filters, offset],
  );

  const { loading } = useOrganSystemsStudiesQuery({
    skip: showInitialData,
    variables,
    onCompleted: data => {
      updateSettings({
        type: 'updateItems',
        value: {
          systems: mapOrganSystems({
            systems: data.nodes,
            wpData: conditionTypes,
          }),
          total: data.pagination.aggregate.count,
        },
      });
    },
    onError: error => {
      addToast({
        content: error.message,
        key: 'dashboard-studies-list-error',
        type: 'negative',
        shouldRemove: true,
      });
    },
  });

  useMemo(() => {
    if (showInitialData) {
      updateSettings({
        type: 'reset',
        value: getInitalData(),
      });
    }
  }, [getInitalData, showInitialData]);

  return (
    <section {...{ id, className: 'grid gap-y-4' }}>
      <Paragraph size="large" tagName="header">
        {translations['Studies by Organ System']}
      </Paragraph>
      <div className="grid md:grid-cols-3 xl:grid-cols-[1fr_1fr_320px] gap-x-16 xl:gap-x-24">
        {loading && offset === 0 ? (
          <Loader fullHeight={false} />
        ) : (
          <OrganSystemsList
            {...{
              loading,
              offset,
              onPageChange: offset =>
                updateSettings({
                  type: 'changeOffset',
                  value: offset,
                }),
              systems,
              total,
            }}
          />
        )}
        <Figure
          {...{
            ...conditionTypesImage,
            className: [
              'hidden md:block row-start-1 col-start-3',
              systems.length ? '' : '-mt-10',
            ].join(' '),
          }}
        />
      </div>
    </section>
  );
};

export default DashboardOrganSystems;
